// Persona-specific prompts for each stage

// Conversational intro prompts for starting new IF sessions
export const introPrompts = {
  student: [
    "What academic spark brought you here today?",
    "Tell me about the idea that's been on your mind...",
    "What problem or question has captured your curiosity?",
    "Share your brain-dump - what are you thinking about?"
  ],
  researcher: [
    "What research question is calling to you?",
    "Talk to me - what's the breakthrough you're chasing?",
    "What hypothesis or insight brought you here?",
    "Give me your brain-dump about the idea you're exploring..."
  ],
  scientist: [
    "What scientific question keeps you up at night?",
    "Tell me about the experiment you're designing...",
    "What hypothesis are you eager to test?",
    "Share your brain-dump - what phenomenon intrigues you?"
  ],
  consultant: [
    "What strategic challenge are you tackling?",
    "Tell me about the problem you're solving for your client...",
    "What business insight sparked your interest today?",
    "Share your thoughts - what opportunity do you see?"
  ],
  innovator: [
    "What innovation idea sparked your visit?",
    "Talk to me - what disruptive concept are you building?",
    "What problem are you passionate about solving?",
    "Give me your brain-dump - what's the big idea?"
  ],
  analyst: [
    "What data story or insight brought you here?",
    "Tell me about the pattern you're investigating...",
    "What analytical question is on your mind?",
    "Share your thoughts - what trend are you exploring?"
  ],
  'design-thinker': [
    "What user need or experience are you reimagining?",
    "Talk to me - what design challenge sparked this?",
    "What empathy-driven insight brought you here?",
    "Give me your brain-dump about the problem you're solving..."
  ],
  'social-innovation': [
    "What social impact idea brought you here today?",
    "Tell me about the change you want to create...",
    "What global challenge is calling to you?",
    "Share your vision - what's the impact you're pursuing?"
  ]
};

export const personaPrompts = {
  student: {
    sparkActivation: [
      "What if you could solve a problem that affects students globally? What academic challenge would you tackle?",
      "What if you combined insights from two different courses you've taken? What new understanding emerges?",
      "What if your thesis could change how people think about your field? What would be the core insight?",
      "What if you had access to any research lab or resource? What would you investigate?",
      "What if you could design the perfect learning experience? What would make it transformative?"
    ],
    mirrorValidation: [
      "If you searched academic databases, what existing research is similar to your idea?",
      "What if similar student projects exist? How is your approach academically rigorous and different?",
      "What if you examined published papers closely? What gap in literature does your idea fill?",
      "If a professor asked 'How is this novel?', what would you cite as precedents vs. your contribution?",
      "What if you presented this at a conference? What makes your methodology or findings unique?"
    ]
  },
  researcher: {
    sparkActivation: [
      "What if you could challenge a fundamental assumption in your field? What would shift?",
      "What if you merged methodologies from disparate disciplines? What breakthrough becomes possible?",
      "What if your research could solve a problem no one else is addressing? What is it?",
      "What if you had unlimited funding and collaboration? What discovery would you pursue?",
      "What if your findings could rewrite textbooks? What paradigm would shift?"
    ],
    mirrorValidation: [
      "If you conducted a literature review, what are the closest research precedents?",
      "What if peer reviewers compared this to existing work? What citations would establish novelty?",
      "What if you identified the theoretical foundations? How does your work advance beyond them?",
      "If this were published, what journal would it fit and why is it publication-worthy?",
      "What if you mapped the research landscape? Where does your work create new territory?"
    ]
  },
  scientist: {
    sparkActivation: [
      "What if you could design an experiment that would definitively prove or disprove a major theory?",
      "What if you discovered a phenomenon that contradicts current scientific understanding?",
      "What if you could combine techniques from different scientific domains? What new method emerges?",
      "What if you had access to any lab equipment or dataset? What would you investigate?",
      "What if your experiment could open an entirely new field of scientific inquiry?"
    ],
    mirrorValidation: [
      "If you modeled this mathematically, what equations or formulas would describe your phenomenon? What existing models come closest?",
      "What if you ran a statistical power analysis - what sample size ensures reproducibility? What prior studies inform your parameters?",
      "If you derived the governing equations, what physical laws or principles underpin your hypothesis? How does this compare to established theory?",
      "What if you quantified your expected effect size? What empirical benchmarks from literature validate your predictions?",
      "If peer reviewers demanded p-values and confidence intervals, what statistical framework proves your findings aren't due to chance?",
      "What if you expressed your hypothesis as a testable equation (e.g., Y = f(X))? What prior empirical work supports each variable relationship?"
    ],
    iteration: [
      "Based on your initial data, how would you refine your mathematical model? What parameters need adjustment?",
      "If your experiment showed unexpected variance, what confounding variables might explain it? How would you control for them?",
      "What if you increased statistical power - would your effect size hold? What does sensitivity analysis reveal?",
      "If you derived error bounds for your measurements, where does uncertainty propagate most? How would you reduce it?",
      "What if you applied dimensional analysis - do your units balance? What does this reveal about your theoretical framework?",
      "If you plotted residuals from your model fit, what systematic patterns emerge? How would you iterate on your hypothesis?"
    ]
  },
  consultant: {
    sparkActivation: [
      "What if you could transform your client's biggest pain point into their greatest advantage?",
      "What if you applied frameworks from adjacent industries? What strategic insight emerges?",
      "What if you redesigned the client's business model from scratch? What would be different?",
      "What if you had complete stakeholder buy-in? What bold recommendation would you make?",
      "What if your solution could 10x their results? What would be the key leverage point?"
    ],
    mirrorValidation: [
      "If you analyzed competitor strategies, what similar approaches exist in the market?",
      "What if consulting firms have proposed comparable solutions? What makes yours strategically distinct?",
      "What if you reviewed case studies? How does your framework improve on best practices?",
      "If a client asked 'Why not use existing methodologies?', what's your differentiation?",
      "What if you benchmarked against industry standards? What's your unique value proposition?"
    ]
  },
  innovator: {
    sparkActivation: [
      "What if you could disrupt an entire industry with one product? What problem would it solve?",
      "What if you combined technologies that have never been merged? What becomes possible?",
      "What if you built a startup that people say 'Why didn't this exist before?' What is it?",
      "What if you had perfect product-market fit from day one? What user need are you fulfilling?",
      "What if your innovation could scale globally in 5 years? What's the vision?"
    ],
    mirrorValidation: [
      "If you searched Product Hunt and tech platforms, what similar products exist?",
      "What if competitors are building something comparable? How is your approach technically superior?",
      "What if investors asked 'What's your moat?', what precedents exist and how do you differentiate?",
      "If this were on Shark Tank, what existing solutions would they compare you to?",
      "What if you analyzed the competitive landscape? What's your unique positioning?"
    ]
  },
  analyst: {
    sparkActivation: [
      "What if you discovered a pattern in data that no one else has seen? What insight would it reveal?",
      "What if you combined multiple data sources in a novel way? What trend emerges?",
      "What if your analysis could predict the future of an industry? What would you forecast?",
      "What if you had access to any dataset? What question would you answer?",
      "What if your insights could drive million-dollar decisions? What would you analyze?"
    ],
    mirrorValidation: [
      "If you reviewed market research reports, what similar analyses have been published?",
      "What if consulting firms have produced comparable insights? What data or methodology makes yours unique?",
      "What if you examined existing dashboards and reports? What analytical angle are you adding?",
      "If stakeholders asked 'Haven't we seen this before?', how would you demonstrate originality?",
      "What if you benchmarked against industry analysts? What's your distinctive analytical lens?"
    ]
  },
  'design-thinker': {
    sparkActivation: [
      "What if you deeply empathized with users' unspoken needs? What hidden pain point would you uncover?",
      "What if you prototyped 10 wildly different solutions? Which one creates the most delight?",
      "What if you reimagined the entire user experience from first principles? What would shift?",
      "What if you combined insights from user research with unexpected inspiration? What emerges?",
      "What if your design could solve a problem people don't even know they have? What is it?"
    ],
    mirrorValidation: [
      "If you analyzed existing design solutions, what patterns and approaches already exist?",
      "What if design firms have tackled similar challenges? How is your human-centered approach different?",
      "What if you reviewed design case studies and portfolios? What makes your empathy-driven insight unique?",
      "If a design critique asked 'What's novel here?', what precedents exist and how do you iterate beyond them?",
      "What if you mapped the design landscape? Where does your solution create new user value?"
    ]
  },
  'social-innovation': {
    sparkActivation: [
      "What if you could solve one UN SDG challenge at scale? Which would create the most impact?",
      "What if you combined local wisdom with global technology? What sustainable solution emerges?",
      "What if your innovation could reach 100 million underserved people? What would you build?",
      "What if you had community support and unlimited partnerships? What global challenge would you tackle?",
      "What if your solution could be replicated across 50 countries? What's the core innovation?"
    ],
    mirrorValidation: [
      "If you researched existing social enterprises and NGOs, what similar initiatives exist?",
      "What if UN agencies have tried comparable approaches? How is your model more scalable or sustainable?",
      "What if you examined successful social innovations? What makes your theory of change unique?",
      "If impact investors asked 'What's different?', how would you demonstrate your innovation beyond existing solutions?",
      "What if you mapped the social innovation ecosystem? Where does your approach create breakthrough impact?"
    ]
  }
};

// Get prompts based on persona and stage
export const getPersonaPrompts = (personaId, stage) => {
  if (!personaId || !personaPrompts[personaId]) {
    // Return default generic prompts if persona not found
    return [
      "What if you could solve any problem without constraints? What would you explore?",
      "What if you combined two completely unrelated concepts? What emerges?",
      "What if the opposite of your initial approach was true? What would that reveal?",
      "What if you had unlimited resources and no limitations? What becomes possible?",
      "What if you could reimagine this from scratch? What would you create?"
    ];
  }
  
  return personaPrompts[personaId][stage] || [];
};

// Get a random intro prompt based on active personas
export const getIntroPrompt = (activePersonas) => {
  if (!activePersonas || activePersonas.length === 0) {
    // Default generic intro prompts
    const defaultIntros = [
      "What idea spark brought you here today?",
      "Talk to me - what are you thinking about?",
      "Give me your brain-dump...",
      "What problem or opportunity is on your mind?"
    ];
    return defaultIntros[Math.floor(Math.random() * defaultIntros.length)];
  }
  
  // Pick a random active persona
  const randomPersona = activePersonas[Math.floor(Math.random() * activePersonas.length)];
  const prompts = introPrompts[randomPersona.id] || introPrompts['student'];
  
  // Return a random prompt from that persona's intro prompts
  return prompts[Math.floor(Math.random() * prompts.length)];
};